import { supabaseClient } from "@/lib/supabase";
import {
    queryOptions,
    useQuery,
    useSuspenseQuery,
    type QueryClient,
    type UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { useEffect } from "react";
import { validate as validateUUID } from "uuid";

import { useAuth } from "../../../utils/auth";
import type { ProfileT } from "@patient-watch/types/database";

const PROFILE_STALE_TIME_MS = 5 * 60 * 1000;

export async function getProfile(id: string): Promise<ProfileT | null> {
    if (!id) {
        throw new Error("No user id provided");
    }

    if (!validateUUID(id)) {
        throw new Error("Invalid user id format");
    }

    const [{ data, error }, { data: userRow, error: userError }] = await Promise.all([
        supabaseClient.from("profiles").select("*").eq("user_id", id).maybeSingle(),
        supabaseClient.from("users").select("user_meta_data").eq("user_id", id).maybeSingle(),
    ]);

    if (error) {
        throw error;
    }
    if (userError) {
        throw userError;
    }

    if (!data) return null;

    return {
        ...data,
        user_meta_data: userRow?.user_meta_data ?? null,
    };
}

export const createProfileQueryKey = (id: string) => ["profile", { user_id: id }];

/** Merge partial profile fields into the cached profile so nav badges update immediately. */
export function patchProfileQueryData(
    queryClient: QueryClient,
    userId: string,
    patch: Partial<ProfileT>
): ProfileT | null | undefined {
    const queryKey = createProfileQueryKey(userId);
    return queryClient.setQueryData<ProfileT | null>(queryKey, (current) =>
        current ? { ...current, ...patch } : current
    );
}

export function invalidateProfileQuery(queryClient: QueryClient, userId: string) {
    return queryClient.invalidateQueries({ queryKey: createProfileQueryKey(userId) });
}

export const selectProfileByIdQueryOptions = (userId: string) => {
    return queryOptions({
        queryKey: createProfileQueryKey(userId),
        // Route loaders may call ensureQueryData while auth is still hydrating.
        // Unlike hooks, imperative query fetches do not honour `enabled`.
        queryFn: () => (userId ? getProfile(userId) : Promise.resolve(null)),
        enabled: Boolean(userId && validateUUID(userId)),
        retry: 1,
        retryDelay: (attemptIndex) => Math.min(500 * 2 ** attemptIndex, 10000),
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        refetchOnReconnect: true,
        staleTime: PROFILE_STALE_TIME_MS,
    });
};

export function useProfileQuery(): UseSuspenseQueryResult<ProfileT | null, Error> {
    const auth = useAuth();
    const profileQuery = useSuspenseQuery(selectProfileByIdQueryOptions(auth.id));
    const isSuperUser = profileQuery.data?.is_super_user ?? false;
    useEffect(() => {
        auth.setIsSuperUser(isSuperUser);
    }, [auth, isSuperUser]);
    return profileQuery;
}

export function useProfileByIdQuery(userId: string) {
    const profileQuery = useSuspenseQuery(selectProfileByIdQueryOptions(userId));
    return profileQuery;
}

// Safe version that doesn't throw on invalid userId - use when userId might be undefined/invalid
export function useProfileByIdQuerySafe(userId: string | undefined | null) {
    const isValid = Boolean(userId && validateUUID(userId));
    return useQuery({
        queryKey: ["profile", { user_id: userId ?? "" }],
        queryFn: () => (isValid ? getProfile(userId!) : null),
        enabled: isValid,
        retry: 1,
        refetchOnWindowFocus: false,
        staleTime: PROFILE_STALE_TIME_MS,
    });
}
